/**
 * Default form data for each node type.
 * Used when creating new nodes to initialize them with sensible defaults.
 * This is the single source of truth - form components use these directly.
 */

import { BreakerFormData } from "../Nodes/BreakerNode/Forms/formSchema";
import { EndFormData } from "../Nodes/EndNode/Forms/EndForm";
import { HTTP_FORMAT_OPTIONS, HTTP_METHOD_OPTIONS, HTTP_PARSER_OPTIONS } from "../Nodes/HttpNode/const";
import { HttpFormData } from "../Nodes/HttpNode/Forms/formSchema";
import { HttpMethodFormData } from "../Nodes/MethodNodes/Forms/HttpMethodForm";
import { MirrorFormData } from "../Nodes/MirrorNode/Forms/MirrorForm";
import { ModifyFieldFormData } from "../Nodes/ModifyFieldNode/Forms/ModifyFieldForm";
import { RepeaterFormData } from "../Nodes/RepeaterNode/Forms/formSchema";
import { SetFormData } from "../Nodes/SetNode/Forms/SetForm";
import { StartFormData } from "../Nodes/StartNode/Forms/StartForm";
import { SubScenarioFormData } from "../Nodes/SubScenarioNode/Forms/SubScenarioForm";
import { WaitFormData } from "../Nodes/WaitNode/Forms/WaitForm";
import { WebhookFormData } from "../Nodes/WebhookNode/Forms/formSchema";
import { NodeType, StepOutcome } from "../types/enums";

const startDefaults: StartFormData = {
    active: true,
    title: "",
    subtitle: "",
    flow: "",
};

const endDefaults: EndFormData = {
    active: true,
    title: "",
    subtitle: "",
    success: StepOutcome.Success,
};

const waitDefaults: WaitFormData = {
    active: true,
    title: "",
    subtitle: "",
    delayValue: 86400, // 24 hours in seconds
    delayUnit: "seconds",
};

const repeaterDefaults: RepeaterFormData = {
    active: true,
    title: "",
    subtitle: "",
    maxRepetition: 1,
};

const breakerDefaults: BreakerFormData = {
    active: true,
    title: "",
    subtitle: "",
    maxRepetition: "1",
};

const httpDefaults: HttpFormData = {
    active: true,
    title: "",
    subtitle: "",
    method: HTTP_METHOD_OPTIONS[1].value,
    format: HTTP_FORMAT_OPTIONS[0].value,
    parser: HTTP_PARSER_OPTIONS[0].value,
    timeout: 40,
    notsend: null,
    convert_data: null,
    endpoint: "",
    headers: [],
    query: [],
    body: [],
    signature: null,
    insecure: false,
};

const webhookDefaults: WebhookFormData = {
    active: true,
    title: "",
    subtitle: "",
    wait: false,
    timeout: 40,
    token: "",
};

const setDefaults: SetFormData = {
    active: true,
    title: "",
    subtitle: "",
    fields: [],
};

const modifyDefaults: ModifyFieldFormData = {
    active: true,
    title: "",
    subtitle: "",
    data: [],
};

const mirrorDefaults: MirrorFormData = {
    active: true,
    title: "",
    subtitle: "",
    mirrorElement: "",
};

const subScenarioDefaults: SubScenarioFormData = {
    active: true,
    title: "",
    subtitle: "",
    scenario: "",
};

const methodsDefaults: HttpMethodFormData = {
    active: true,
    title: "",
    subtitle: "",
    method: "",
    format: "",
    timeout: 0,
    endpoint: "",
    query: [],
    header: [],
    body: [],
};

export type NodeFormData =
    | StartFormData
    | EndFormData
    | WaitFormData
    | RepeaterFormData
    | BreakerFormData
    | HttpFormData
    | WebhookFormData
    | SetFormData
    | ModifyFieldFormData
    | MirrorFormData
    | SubScenarioFormData
    | HttpMethodFormData;

/**
 * Get default form data for a given node type
 */
export function getDefaultFormData(nodeType: NodeType | string): NodeFormData {
    switch (nodeType) {
        case NodeType.Start:
            return startDefaults;
        case NodeType.End:
            return endDefaults;
        case NodeType.Wait:
            return waitDefaults;
        case NodeType.Repeater:
            return repeaterDefaults;
        case NodeType.Breaker:
            return breakerDefaults;
        case NodeType.Http:
            return httpDefaults;
        case NodeType.Webhook:
            return webhookDefaults;
        case NodeType.Set:
            return setDefaults;
        case NodeType.Modify:
            return modifyDefaults;
        case NodeType.Mirror:
            return mirrorDefaults;
        case NodeType.SubScenario:
            return subScenarioDefaults;
        case NodeType.Methods:
            return methodsDefaults;
        default:
            return {
                active: true,
                title: "",
                subtitle: "",
            } as unknown as NodeFormData;
    }
}

/**
 * Serialize form data for dirty-state comparison.
 * Treats undefined/null as "" so that Lexical's initial onChange (which emits ""
 * for empty fields) doesn't differ from getValues() (which may return undefined).
 */
export const serializeForDirtyCheck = (data: unknown): string =>
    JSON.stringify(data, (_, v) => (v === undefined || v === null ? "" : v));
